/**
 * Shared funding appeal contact form markup (directory hydration + admin preview).
 * Uses Tailwind-style class names; pair with `FUNDING_APPEAL_CONTACT_FORM_CSS` when Tailwind is not loaded.
 */

const DEFAULT_WWW_URL = "https://www.mixitone.com";

export const FUNDING_APPEAL_CONTACT_FORM_FORM_CLASS = "mx-auto mt-4 max-w-md space-y-4 text-left";

function escapeHtml(input: string): string {
  return input
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

function escapeAttr(input: string): string {
  return escapeHtml(input);
}

export type FundingAppealContactFormMarkupOptions = {
  appealId: string;
  submitLabel?: string;
  wwwUrl?: string;
  /** Admin preview: enabled fields, non-submitting button, turnstile placeholder. */
  preview?: boolean;
};

const DEFAULT_SUCCESS_INNER = `
        <div id="form-success" class="hidden rounded-lg border border-green-200 bg-green-50 p-6 text-center">
          <h3 class="mb-2 text-xl font-bold text-green-800">Thank you!</h3>
          <p class="text-green-700">Your message has been sent to the club.</p>
        </div>`;

/**
 * Inner HTML for the appeal contact form (injected into `<form>` on the directory site).
 */
export function fundingAppealContactFormInnerHtml(options: FundingAppealContactFormMarkupOptions): string {
  const appealId = escapeAttr(options.appealId.trim());
  const submitLabel = escapeHtml(options.submitLabel?.trim() || "Send message");
  const wwwUrl = escapeAttr(options.wwwUrl?.trim() || DEFAULT_WWW_URL);
  const preview = options.preview === true;
  const successBlock = preview ? "" : DEFAULT_SUCCESS_INNER;
  const buttonType = preview ? "button" : "submit";
  const turnstileSlot = preview
    ? '<div class="turnstile-slot turnstile-preview-placeholder" aria-hidden="true"></div>'
    : '<div class="turnstile-slot"></div>';

  return `
        <div id="form-error" class="hidden rounded-lg border border-red-200 bg-red-50 p-4">
          <p id="form-error-text" class="text-red-800"></p>
        </div>
        ${successBlock}
        <div>
          <label for="appeal-contact-name-${appealId}" class="mb-2 block text-sm font-medium text-gray-900">Name <span class="text-red-500">*</span></label>
          <input type="text" id="appeal-contact-name-${appealId}" name="name" required maxlength="200" class="block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500" placeholder="Your name" />
        </div>
        <div>
          <label for="appeal-contact-email-${appealId}" class="mb-2 block text-sm font-medium text-gray-900">Email <span class="text-red-500">*</span></label>
          <input type="email" id="appeal-contact-email-${appealId}" name="email" required maxlength="200" class="block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500" placeholder="your.email@example.com" />
        </div>
        <div>
          <label for="appeal-contact-message-${appealId}" class="mb-2 block text-sm font-medium text-gray-900">Message <span class="text-red-500">*</span></label>
          <textarea id="appeal-contact-message-${appealId}" name="message" required rows="5" minlength="10" maxlength="5000" class="block w-full rounded-lg border border-gray-300 bg-gray-50 px-4 py-3 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500" placeholder="Your message..."></textarea>
        </div>
        ${turnstileSlot}
        <button type="${buttonType}" class="w-full rounded-lg bg-cyan-700 px-6 py-3 font-semibold text-white hover:bg-cyan-800 focus:outline-none focus:ring-2 focus:ring-cyan-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50">${submitLabel}</button>
        <p class="text-center text-xs text-gray-500">
          <a href="${wwwUrl}/privacy" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
          and
          <a href="${wwwUrl}/terms" class="text-blue-600 hover:underline" target="_blank" rel="noopener noreferrer">Terms of Service</a>
          apply.
        </p>
      `;
}

/** Full `<form>` element for admin landing preview (same markup as directory hydration). */
export function renderFundingAppealContactFormHtml(options: FundingAppealContactFormMarkupOptions): string {
  const previewAttr = options.preview ? ' onsubmit="return false"' : "";
  return `<form class="${FUNDING_APPEAL_CONTACT_FORM_FORM_CLASS}"${previewAttr}>${fundingAppealContactFormInnerHtml(options)}</form>`;
}

/** CSS for contact form utility classes when Tailwind is not on the page (e.g. admin iframe preview). */
export const FUNDING_APPEAL_CONTACT_FORM_CSS = `
  .mx-auto { margin-left: auto; margin-right: auto; }
  .mt-4 { margin-top: 1rem; }
  .max-w-md { max-width: 28rem; }
  .space-y-4 > :not([hidden]) ~ :not([hidden]) { margin-top: 1rem; }
  .text-left { text-align: left; }
  .text-center { text-align: center; }
  .hidden { display: none; }
  .block { display: block; }
  .w-full { width: 100%; }
  .mb-2 { margin-bottom: 0.5rem; }
  .text-sm { font-size: 0.875rem; line-height: 1.25rem; }
  .text-xs { font-size: 0.75rem; line-height: 1rem; }
  .text-xl { font-size: 1.25rem; line-height: 1.75rem; }
  .font-medium { font-weight: 500; }
  .font-semibold { font-weight: 600; }
  .font-bold { font-weight: 700; }
  .text-gray-900 { color: #111827; }
  .text-gray-500 { color: #6b7280; }
  .text-red-500 { color: #ef4444; }
  .text-red-800 { color: #991b1b; }
  .text-green-700 { color: #15803d; }
  .text-green-800 { color: #166534; }
  .text-white { color: #ffffff; }
  .text-blue-600 { color: #2563eb; }
  .rounded-lg { border-radius: 0.5rem; }
  .border { border-width: 1px; border-style: solid; }
  .border-gray-300 { border-color: #d1d5db; }
  .border-red-200 { border-color: #fecaca; }
  .border-green-200 { border-color: #bbf7d0; }
  .bg-gray-50 { background-color: #f9fafb; }
  .bg-red-50 { background-color: #fef2f2; }
  .bg-green-50 { background-color: #f0fdf4; }
  .bg-cyan-700 { background-color: #0e7490; }
  .p-4 { padding: 1rem; }
  .p-6 { padding: 1.5rem; }
  .px-4 { padding-left: 1rem; padding-right: 1rem; }
  .px-6 { padding-left: 1.5rem; padding-right: 1.5rem; }
  .py-3 { padding-top: 0.75rem; padding-bottom: 0.75rem; }
  input.block, textarea.block { box-sizing: border-box; }
  input:focus, textarea:focus { outline: 2px solid #3b82f6; outline-offset: 0; border-color: #3b82f6; }
  .turnstile-preview-placeholder { min-height: 65px; border: 1px solid #d1d5db; border-radius: 4px; background: #f9fafb; }
  button.w-full { cursor: pointer; border: none; }
  button.w-full:hover { background-color: #0e7490; filter: brightness(0.92); }
  button.w-full:disabled { cursor: not-allowed; opacity: 0.5; }
  a.text-blue-600 { text-decoration: none; }
  a.text-blue-600:hover { text-decoration: underline; }
`.trim();
